import { resolveDateEnd, resolveDateRange } from "../../Utils/resolveDateRange";

/**
 * What a stat tile computes for a comparison + aggregation pair, in words the editor shows.
 *
 * The backend defines the tile's window by the comparison, not by the Date Range step:
 * every "Latest vs ..." comparison reads the latest session on or before the end date and
 * ignores the start date. Only "Selected Date Range" aggregates the whole window. A `sum`
 * aggregation forces "Selected Date Range" whatever comparison the tile stores. The editor
 * states this next to the controls, so a coach does not read a 21-day average off a tile
 * that shows one session.
 */
export interface StatWindowHint {
  /** What the tile's main number is. */
  value: string;
  /** How the Date Range step applies to the tile. */
  dateRange: string;
  /** The Date Range header summary: the full range, or "as of <end>" when only the end applies. */
  dateSummary: string;
}

interface DateFields {
  showAdvancedDate?: boolean;
  startDateType?: string;
  startDateAbsolute?: string | null;
  startDateOffset?: number;
  endDateType?: string;
  endDateAbsolute?: string | null;
  endDateOffset?: number;
  dateRange?: string;
}

const AGG_WORD: Record<string, string> = { avg: "Average", best: "Best", sum: "Sum" };

const WHOLE_WINDOW_KEY = "selected_date_range";
const WEEK_KEY = /^latest_vs_week_minus_(\d+)$/;
const LAST_N_KEY = /^latest_vs_last(\d+)$/;

function comparedTo(comparisonType: string): string {
  const week = WEEK_KEY.exec(comparisonType);
  if (week) return `the week that ended ${week[1]} week${week[1] === "1" ? "" : "s"} earlier`;
  const lastN = LAST_N_KEY.exec(comparisonType);
  if (lastN) return `the average of the ${lastN[1]} sessions before it`;
  if (comparisonType === "latest_vs_all") return "the average of all prior sessions";
  return "the session before it";
}

export function describeStatWindow(
  comparisonType: string,
  metricAgg: string,
  fields: DateFields,
): StatWindowHint {
  const agg = AGG_WORD[metricAgg] ?? "Average";
  if (metricAgg === "sum" || comparisonType === WHOLE_WINDOW_KEY) {
    return {
      value: `${agg} of every session in the date range. No comparison.`,
      dateRange: "The tile aggregates every session in this window.",
      dateSummary: resolveDateRange(fields),
    };
  }
  return {
    value:
      `The latest session on or before the end date, compared with ${comparedTo(comparisonType)}. ` +
      `${agg} applies across the selected athletes on that session, not across dates.`,
    dateRange:
      "Only the end date applies: the tile shows the latest session on or before it. " +
      "To average or find the best value over a window, set Comparison to \"Selected Date Range\".",
    dateSummary: `as of ${resolveDateEnd(fields)}`,
  };
}
